import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import VideoIcon from '@atlaskit/icon/core/video';
import GenericTile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { convertVideoDuration } from '../search-graph-result/search-graph-video/convert-video-duration';
import { messages } from '../search-graph-result/search-graph-video/messages';

import type { searchLoomVideo_AtlassianSearchPage$key } from './__generated__/searchLoomVideo_AtlassianSearchPage.graphql';

const LoomVideoIcon = (): React.JSX.Element => (
	<GenericTile label="" size="medium" testId="loom-video-icon">
		<VideoIcon label="" color={token('color.icon.brand')} size="medium" spacing="none" />
	</GenericTile>
);

export const isSearchLoomVideo = (node: { __typename?: string }): boolean =>
	node.__typename === 'SearchResultLoomVideo';

export const SearchLoomVideo = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	selectedProduct,
	mainContentWidth,
	selectedSites,
}: SearchResultProps<searchLoomVideo_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchLoomVideo_AtlassianSearchPage on SearchResultLoomVideo {
				id
				title
				description
				type
				url
				durationSeconds
			}
		`,
		node,
	);
	const { formatMessage } = useIntl();
	const [, , , contentId] = useMemo(() => getProductAndIDFromARI(data.id), [data.id]);
	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: data.type,
				entityType: data.type,
				contentId,
				product: ProductKeys.Loom,
				...getSiteMetadata(data.id, cloudId, selectedSites),
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[cloudId, contentId, data.id, data.type, selectedSites],
	);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	const duration = convertVideoDuration(data.durationSeconds, {
		readable: true,
		formatMessage,
	});
	const description = data.description ? (
		<DescriptionBody description={processExcerpt(data.description)} />
	) : null;

	return (
		<SearchResult
			id={data.id}
			product={ProductKeys.Loom}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={enrichExcerpt(data.title)}
			icon={<LoomVideoIcon />}
			headerMetadataParts={[formatMessage(messages.videoType), ['duration', duration]]}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};
